/**
 * Feb 2026 · Pending Work Order Statement — sister report to Pending
 * Sales Order Statement. Two tabs (WO-Wise · Product-Wise) with:
 *   • Delayed-first + earliest-date sort
 *   • Search across every displayed field
 *   • CSV + PDF downloads (server-side PDF ships with letterhead + Sum row)
 *   • **Print Preview overlay** — white A4 sheet with letterhead + bordered
 *     table + BOLD SUM TOTAL row at the bottom, mirroring the Pending
 *     Sales Order print blueprint (text left, numbers right, comma fmt).
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  PrinterIcon, DownloadSimpleIcon, MagnifyingGlassIcon,
  ArrowsClockwiseIcon, FilePdfIcon, XIcon,
} from "@phosphor-icons/react";
import { exportToCSV } from "@/lib/csv";
import { COMPANY, getPrintCompany } from "@/config/company";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const TODAY = () => new Date().toISOString().slice(0, 10);
const EMPTY_TOT = { so_eso: 0, production: 0, produced: 0, balance: 0 };

function riskChip(daysToDelivery, delayed) {
  if (delayed) return { label: "Delayed", cls: "bg-red-500/15 text-red-300 border-red-500/40", print: "text-red-700" };
  if (daysToDelivery == null) return { label: "—", cls: "text-zinc-500", print: "" };
  if (daysToDelivery <= 7) return { label: `${daysToDelivery}d`, cls: "bg-amber-500/15 text-amber-300 border-amber-500/40", print: "text-amber-700" };
  return { label: `${daysToDelivery}d`, cls: "text-zinc-400", print: "" };
}

export default function PendingWorkOrder() {
  const [tab, setTab] = useState("wo");
  const [stmt, setStmt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);

  const co = useMemo(() => getPrintCompany() || COMPANY, []);

  // PENDING-WO-STATEMENT-01 · the server is the single calculation authority (rows, totals, CSV, PDF).
  const load = async (query) => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/pending-work-order", { params: { q: query || "" } });
      setStmt(data);
    } catch (e) { toast.error("Failed to load pending WO data"); }
    setLoading(false);
  };
  useEffect(() => {
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const woRows = stmt?.wo_rows || [];
  const productRows = stmt?.product_rows || [];
  const nmRows = stmt?.not_measurable_rows || [];
  const totals = (tab === "wo" ? stmt?.wo_totals : stmt?.product_totals) || EMPTY_TOT;
  const overdueCount = stmt?.overdue_count || 0;


  const csvExport = () => {
    if (tab === "wo") {
      exportToCSV("pending-work-order-wo-wise.csv", [...woRows, ...nmRows], [
        { key: "wo_no", label: "WO No" }, { key: "date", label: "Date" },
        { key: "source_order_no", label: "SO/ESO No" }, { key: "product_code", label: "Product Code" },
        { key: "so_eso_qty", label: "SO/ESO Qty" },
        { key: "production_qty", label: "Production Qty" }, { key: "produced_qty", label: "Already Produced" },
        { key: "balance_qty", label: "Balance Pending" }, { key: "target_date", label: "Target Date" },
        { key: "status", label: "Status" }, { key: "reason", label: "Reason" },
      ]);
    } else {
      exportToCSV("pending-work-order-product-wise.csv", productCsvRows(productRows, nmRows), [
        { key: "product_code", label: "Product Code" }, { key: "wo_count", label: "WOs" },
        { key: "customers", label: "Customers" },
        { key: "so_eso_qty", label: "SO/ESO Qty" },
        { key: "production_qty", label: "Production Qty" }, { key: "produced_qty", label: "Already Produced" },
        { key: "balance_qty", label: "Balance Pending" }, { key: "earliest_date", label: "Earliest Date" },
      ]);
    }
  };

  const pdfDownload = async () => {
    try {
      const res = await api.get("/reports/pending-work-order.pdf", { params: { tab, q }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `pending-work-order-${tab}-wise-${TODAY()}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error("PDF download failed"); }
  };

  // Feb 2026 · Mirrors Pending SO print pipeline — hidden iframe, A4
  // Portrait, inline styles + inherited stylesheets. The overlay
  // PrintSheet stays in-DOM during print (no print-hide on it) so
  // that IS what hits the paper.
  const doPrint = () => {
    const sheet = document.getElementById("pwo-print-sheet");
    if (!sheet) { window.print(); return; }
    const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
      .map(node => node.outerHTML).join("\n");
    document.getElementById("__pwo_print_iframe")?.remove();
    const iframe = document.createElement("iframe");
    iframe.id = "__pwo_print_iframe";
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
    document.body.appendChild(iframe);
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Pending Work Order</title>
      <style>
        @page { size: A4 portrait; margin: 8mm; }
        @page { size: 210mm 297mm; margin: 8mm; }
        html, body {
          background: #fff !important; color: #000 !important;
          margin: 0 !important; padding: 0 !important;
          width: 210mm; overflow: visible;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      </style>
      ${styles}
      <style>
        #pwo-print-sheet { position: static !important; width: 100% !important; min-height: auto !important; box-shadow: none !important; padding: 0 !important; background: #fff !important; color: #000 !important; }
        table { color: #000 !important; page-break-inside: auto; }
        table thead { background: #eee !important; color: #000 !important; display: table-header-group; }
        table tr { page-break-inside: avoid; }
        [class*="text-emerald"], [class*="text-amber"], [class*="text-sky"], [class*="text-white"], [class*="text-red"], [class*="text-blue"], [class*="text-cyan"], .text-\\[var\\(--accent\\)\\] { color: #000 !important; }
      </style>
    </head><body>${sheet.outerHTML}</body></html>`;
    iframe.srcdoc = html;
    iframe.addEventListener("load", () => {
      requestAnimationFrame(() => requestAnimationFrame(() => {
        try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) { toast.error("Print failed"); }
        setTimeout(() => iframe.remove(), 1000);
      }));
    }, { once: true });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between print-hide">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight" data-testid="pwo-title">Pending Work Order Statement</h1>
          <div className="text-xs text-zinc-500 mt-1">Balance Pending = Production Qty − Already Produced. Delayed WOs sort to the top.</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => load(q)} className="h-9 px-3 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm rounded-sm flex items-center gap-2" data-testid="pwo-reload">
            <ArrowsClockwiseIcon size={13} weight="bold" />Refresh
          </button>
          <button onClick={csvExport} className="h-9 px-3 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm rounded-sm flex items-center gap-2" data-testid="pwo-csv">
            <DownloadSimpleIcon size={13} weight="bold" />Excel / CSV
          </button>
          <button onClick={pdfDownload} className="h-9 px-3 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm rounded-sm flex items-center gap-2" data-testid="pwo-pdf">
            <FilePdfIcon size={13} weight="bold" />Download PDF
          </button>
          <button onClick={() => setPreviewOpen(true)} className="h-9 px-3 bg-[var(--accent)] hover:opacity-90 text-white text-sm font-semibold rounded-sm flex items-center gap-2" data-testid="pwo-print">
            <PrinterIcon size={13} weight="bold" />Print Preview
          </button>
        </div>
      </div>

      {previewOpen && (
        <PrintPreviewOverlay
          company={co}
          woRows={woRows}
          productRows={productRows}
          woTotals={stmt?.wo_totals || EMPTY_TOT}
          productTotals={stmt?.product_totals || EMPTY_TOT}
          nmRows={nmRows}
          onClose={() => setPreviewOpen(false)}
          onPrint={doPrint}
        />
      )}

      {/* KPI strip + tab switcher */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 print-hide">
        <Kpi label="Pending WOs" value={woRows.length} tone="accent" tid="pwo-kpi-count" />
        <Kpi label="Overdue" value={fmt(overdueCount)} tone="red" tid="pwo-kpi-overdue" />
        <Kpi label="Balance Pending (m)" value={fmt(stmt?.wo_totals?.balance || 0)} tone="amber" tid="pwo-kpi-pending" />
        <Kpi label="Product Codes" value={productRows.length} tone="cyan" tid="pwo-kpi-articles" />
      </div>

      <div className="flex items-center gap-2 print-hide">
        <div className="inline-flex rounded-sm border border-[var(--border)] overflow-hidden text-xs uppercase tracking-widest">
          {[["wo", "WO-Wise Pending"], ["product", "Product-Wise Pending"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`pwo-tab-${k}`} className={`px-4 py-2 ${tab === k ? "bg-[var(--accent)] text-white" : "text-zinc-400 hover:bg-[var(--bg-2)]"}`}>{l}</button>
          ))}
        </div>
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlassIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={tab === "wo" ? "Search WO / SO / Customer / Product Code…" : "Search Product Code / Customer…"} className="h-9 pl-8 pr-3 w-full border border-[var(--border)] bg-[var(--bg-1)] rounded-sm text-sm" data-testid="pwo-search" />
        </div>
      </div>

      <div className="tactical-card overflow-x-auto">
        {tab === "wo" ? <WoTable rows={woRows} totals={totals} loading={loading} /> : <ProductTable rows={productRows} totals={totals} loading={loading} />}
        <NotMeasurableTable rows={nmRows} />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * Print Preview Overlay + PrintSheet (A4 Portrait blueprint)
 *   • White paper card, letterhead (name/tagline/address)
 *   • Report title "PENDING WORK ORDER STATEMENT · {tab}"
 *   • Bordered table (bg-gray-200 header) — text-left / numbers-right
 *     with Indian comma formatting
 *   • Bold Sum Total row at the BOTTOM
 * ─────────────────────────────────────────────────────────────── */
function PrintPreviewOverlay({ company, woRows, productRows, woTotals, productTotals, nmRows, onClose, onPrint }) {
  return (
    <div className="fixed inset-0 z-[10000] flex flex-col" data-testid="pwo-preview-overlay">
      <div className="absolute inset-0 bg-black/80 print-hide" />
      <div className="relative flex items-center justify-between px-6 py-3 bg-[var(--bg-1)] border-b border-[var(--border)] print-hide">
        <div>
          <div className="overline text-[10px]">Print Preview · A4 Portrait · WO-Wise + Product-Wise</div>
          <div className="font-display text-sm font-bold text-white">Pending Work Order Statement</div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="pwo-preview-print" onClick={onPrint} className="h-9 px-4 bg-[var(--accent)] hover:opacity-90 text-white text-sm font-semibold rounded-sm flex items-center gap-2">
            <PrinterIcon size={13} weight="bold" />Print
          </button>
          <button data-testid="pwo-preview-close" onClick={onClose} className="h-9 px-3 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm font-semibold rounded-sm flex items-center gap-2">
            <XIcon size={13} weight="bold" />Close
          </button>
        </div>
      </div>
      <div className="relative flex-1 overflow-auto p-6 bg-zinc-800">
        <PrintSheet company={company} woRows={woRows} productRows={productRows} woTotals={woTotals} productTotals={productTotals} nmRows={nmRows} />
      </div>
    </div>
  );
}

function PrintSheet({ company, woRows, productRows, woTotals, productTotals, nmRows = [] }) {
  // PENDING-WO-STATEMENT-01 · section totals come from the server statement.

  return (
    <div id="pwo-print-sheet" className="mx-auto bg-white text-black shadow-2xl" style={{ width: "794px", minHeight: "1123px", padding: "32px 36px" }}>
      {/* Letterhead */}
      <div className="text-center border-b-2 border-black pb-2 mb-3">
        <div className="font-black text-xl uppercase tracking-wide">{company.name}</div>
        {company.tagline && <div className="text-[10px] italic">{company.tagline}</div>}
        <div className="text-[10px] whitespace-pre-line">{company.address}</div>
        <div className="text-lg font-bold mt-2 uppercase">Pending Work Order Statement</div>
        <div className="text-[10px]">Generated: {new Date().toLocaleString("en-IN")}</div>
      </div>

      {/* Section 1 · WO-Wise */}
      <PrintWoSection rows={woRows} totals={woTotals} />

      {/* Section 2 · Product-Wise (page-break for cleaner print) */}
      <div style={{ pageBreakBefore: "auto", marginTop: "16px" }}>
        <PrintProductSection rows={productRows} totals={productTotals} />
      </div>

      {nmRows.length > 0 && (
        <div style={{ marginTop: "16px" }} data-testid="pwo-print-not-measurable">
          <div className="font-bold text-[11px] mb-1">Pending Not Measurable · {nmRows.length} active WO(s) · excluded from totals</div>
          <table className="w-full border-collapse text-[9px]">
            <thead><tr>{["WO No", "SO/ESO No", "Product Code", "Authorised", "Reason"].map(h => (
              <th key={h} className="border border-gray-400 px-1.5 py-1 text-left bg-gray-200">{h}</th>))}</tr></thead>
            <tbody>{nmRows.map(r => (
              <tr key={r.id}>
                <td className="border border-gray-400 px-1.5 py-1">{r.wo_no}</td>
                <td className="border border-gray-400 px-1.5 py-1">{r.source_order_no || "—"}</td>
                <td className="border border-gray-400 px-1.5 py-1">{r.product_code || "—"}</td>
                <td className="border border-gray-400 px-1.5 py-1 text-right">{fmt(r.production_qty)}</td>
                <td className="border border-gray-400 px-1.5 py-1">{r.reason}</td>
              </tr>))}</tbody>
          </table>
        </div>
      )}

      <div className="mt-6 pt-2 border-t border-black text-[9px] text-gray-700 flex justify-between">
        <span>Confidential · For internal use only</span>
        <span>Portrait · A4</span>
      </div>
    </div>
  );
}

function PrintWoSection({ rows, totals }) {
  return (
    <div style={{ pageBreakInside: "auto" }}>
      <div className="text-[11px] font-bold uppercase tracking-widest mb-1 border-b border-black pb-0.5">
        WO-wise Pending ({rows.length} work orders)
      </div>
      <table className="w-full text-[9px] border-collapse">
        <thead>
          <tr className="bg-gray-200">
            <th className="border border-gray-400 px-1.5 py-1 text-left">WO No</th>
            <th className="border border-gray-400 px-1.5 py-1 text-left">Date</th>
            <th className="border border-gray-400 px-1.5 py-1 text-left">SO/ESO No</th>
            <th className="border border-gray-400 px-1.5 py-1 text-left">Product Code</th>
            <th className="border border-gray-400 px-1.5 py-1 text-right">SO/ESO Qty</th>
            <th className="border border-gray-400 px-1.5 py-1 text-right">Production</th>
            <th className="border border-gray-400 px-1.5 py-1 text-right">Produced</th>
            <th className="border border-gray-400 px-1.5 py-1 text-right">Balance</th>
            <th className="border border-gray-400 px-1.5 py-1 text-left">Target Date</th>
            <th className="border border-gray-400 px-1.5 py-1 text-left">Risk</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={10} className="border border-gray-400 px-2 py-3 text-center italic">No pending work orders.</td></tr>
          )}
          {rows.map((r, i) => {
            const risk = riskChip(r.days_to_delivery, r.delayed);
            return (
              <tr key={i}>
                <td className="border border-gray-400 px-1.5 py-1 font-mono">{r.wo_no}</td>
                <td className="border border-gray-400 px-1.5 py-1">{r.date || "—"}</td>
                <td className="border border-gray-400 px-1.5 py-1 font-mono">{r.source_order_no || "—"}</td>
                <td className="border border-gray-400 px-1.5 py-1 font-mono">{r.product_code || "—"}</td>
                <td className="border border-gray-400 px-1.5 py-1 text-right font-mono tabular-nums">{fmt(r.so_eso_qty)}</td>
                <td className="border border-gray-400 px-1.5 py-1 text-right font-mono tabular-nums">{fmt(r.production_qty)}</td>
                <td className="border border-gray-400 px-1.5 py-1 text-right font-mono tabular-nums">{fmt(r.produced_qty)}</td>
                <td className="border border-gray-400 px-1.5 py-1 text-right font-mono tabular-nums font-bold">{fmt(r.balance_qty)}</td>
                <td className="border border-gray-400 px-1.5 py-1 font-mono">{r.target_date || "—"}</td>
                <td className={`border border-gray-400 px-1.5 py-1 text-[8px] uppercase font-semibold ${risk.print}`}>{risk.label}</td>
              </tr>
            );
          })}
          {rows.length > 0 && (
            <tr className="bg-gray-300 font-bold border-y-2 border-black">
              <td className="border border-gray-400 px-1.5 py-1 uppercase" colSpan={4}>Sum Total</td>
              <td className="border border-gray-400 px-1.5 py-1 text-right font-mono tabular-nums">{fmt(totals.so_eso)}</td>
              <td className="border border-gray-400 px-1.5 py-1 text-right font-mono tabular-nums">{fmt(totals.production)}</td>
              <td className="border border-gray-400 px-1.5 py-1 text-right font-mono tabular-nums">{fmt(totals.produced)}</td>
              <td className="border border-gray-400 px-1.5 py-1 text-right font-mono tabular-nums">{fmt(totals.balance)}</td>
              <td className="border border-gray-400 px-1.5 py-1" colSpan={2}></td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function PrintProductSection({ rows, totals }) {
  return (
    <div style={{ pageBreakInside: "auto" }}>
      <div className="text-[11px] font-bold uppercase tracking-widest mb-1 border-b border-black pb-0.5">
        Product-wise Pending ({rows.length} product codes)
      </div>
      <table className="w-full text-[10px] border-collapse">
        <thead>
          <tr className="bg-gray-200">
            <th className="border border-gray-400 px-2 py-1 text-left">Product Code</th>
            <th className="border border-gray-400 px-2 py-1 text-right">WOs</th>
            <th className="border border-gray-400 px-2 py-1 text-left">Customers</th>
            <th className="border border-gray-400 px-2 py-1 text-right">SO/ESO Qty</th>
            <th className="border border-gray-400 px-2 py-1 text-right">Production</th>
            <th className="border border-gray-400 px-2 py-1 text-right">Produced</th>
            <th className="border border-gray-400 px-2 py-1 text-right">Balance</th>
            <th className="border border-gray-400 px-2 py-1 text-left">Earliest Date</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={8} className="border border-gray-400 px-2 py-3 text-center italic">No pending items.</td></tr>
          )}
          {rows.map((r, i) => (
            <tr key={r.product_code}>
              <td className="border border-gray-400 px-2 py-1 font-mono">{r.product_code}</td>
              <td className="border border-gray-400 px-2 py-1 text-right font-mono">{r.wo_count}</td>
              <td className="border border-gray-400 px-2 py-1">{r.customers || "—"}</td>
              <td className="border border-gray-400 px-2 py-1 text-right font-mono tabular-nums">{fmt(r.so_eso_qty)}</td>
              <td className="border border-gray-400 px-2 py-1 text-right font-mono tabular-nums">{fmt(r.production_qty)}</td>
              <td className="border border-gray-400 px-2 py-1 text-right font-mono tabular-nums">{fmt(r.produced_qty)}</td>
              <td className="border border-gray-400 px-2 py-1 text-right font-mono tabular-nums font-bold">{fmt(r.balance_qty)}</td>
              <td className="border border-gray-400 px-2 py-1 font-mono">{r.earliest_date || "—"}</td>
            </tr>
          ))}
          {rows.length > 0 && (
            <tr className="bg-gray-300 font-bold border-y-2 border-black">
              <td className="border border-gray-400 px-2 py-1 uppercase" colSpan={3}>Sum Total</td>
              <td className="border border-gray-400 px-2 py-1 text-right font-mono tabular-nums">{fmt(totals.so_eso)}</td>
              <td className="border border-gray-400 px-2 py-1 text-right font-mono tabular-nums">{fmt(totals.production)}</td>
              <td className="border border-gray-400 px-2 py-1 text-right font-mono tabular-nums">{fmt(totals.produced)}</td>
              <td className="border border-gray-400 px-2 py-1 text-right font-mono tabular-nums">{fmt(totals.balance)}</td>
              <td className="border border-gray-400 px-2 py-1"></td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * On-screen tables (dark theme, rollup at TOP for quick scan)
 * ─────────────────────────────────────────────────────────────── */
function WoTable({ rows, totals, loading }) {
  return (
    <table className="w-full text-sm">
      <thead>
        {rows.length > 0 && (
          <tr data-testid="pwo-rollup" className="border-b-2 border-amber-400/50 bg-amber-500/5 text-[11px] font-mono">
            <th className="text-left px-3 py-2.5" colSpan={4}>
              <div className="flex flex-col leading-tight">
                <span className="text-[9px] uppercase tracking-widest text-zinc-500">Sum · {rows.length} pending</span>
                <span className="font-display text-[13px] text-white">Totals</span>
              </div>
            </th>
            <th className="text-right px-3 py-2.5 text-sky-300 tabular-nums text-base font-bold">{fmt(totals.so_eso)}</th>
            <th className="text-right px-3 py-2.5 text-cyan-300 tabular-nums text-base font-bold">{fmt(totals.production)}</th>
            <th className={`text-right px-3 py-2.5 tabular-nums text-base font-bold ${totals.produced > 0 ? "text-emerald-300" : "text-white"}`}>{fmt(totals.produced)}</th>
            <th className="text-right px-3 py-2.5 text-amber-300 tabular-nums text-base font-bold">{fmt(totals.balance)}</th>
            <th colSpan={2}></th>
          </tr>
        )}
        <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
          <th className="text-left px-3 py-3 font-medium">WO No</th>
          <th className="text-left px-3 py-3 font-medium">Date</th>
          <th className="text-left px-3 py-3 font-medium">SO/ESO No</th>
          <th className="text-left px-3 py-3 font-medium">Product Code</th>
          <th className="text-right px-3 py-3 font-medium">SO/ESO Qty</th>
          <th className="text-right px-3 py-3 font-medium">Production Qty</th>
          <th className="text-right px-3 py-3 font-medium">Already Produced</th>
          <th className="text-right px-3 py-3 font-medium">Balance Pending</th>
          <th className="text-left px-3 py-3 font-medium">Target Date</th>
          <th className="text-left px-3 py-3 font-medium">Risk</th>
        </tr>
      </thead>
      <tbody>
        {loading && <tr><td colSpan={10} className="text-center py-16 text-zinc-500">Loading…</td></tr>}
        {!loading && rows.length === 0 && <tr><td colSpan={10} className="text-center py-16 text-zinc-500">No pending Work Orders 🎉</td></tr>}
        {rows.map((r, i) => {
          const risk = riskChip(r.days_to_delivery, r.delayed);
          return (
            <tr key={r.id} data-testid={`pwo-wo-row-${i}`} className={`border-t border-[var(--border)] ${r.delayed ? "bg-red-500/5" : "hover:bg-[var(--bg-2)]/30"}`}>
              <td className="px-3 py-3 font-mono text-[var(--accent)]">{r.wo_no}</td>
              <td className="px-3 py-3 font-mono text-zinc-400">{r.date || "—"}</td>
              <td className="px-3 py-3 font-mono text-zinc-300" title={r.customer}>{r.source_order_no || "—"}</td>
              <td className="px-3 py-3 font-mono text-emerald-300 truncate max-w-[220px]" title={r.product_code}>{r.product_code || "—"}</td>
              <td className="px-3 py-3 text-right font-mono text-sky-300 tabular-nums" title={r.source_order_no}>{fmt(r.so_eso_qty)}</td>
              <td className="px-3 py-3 text-right font-mono text-cyan-300 tabular-nums">{fmt(r.production_qty)}</td>
              <td className={`px-3 py-3 text-right font-mono tabular-nums ${r.produced_qty > 0 ? "text-emerald-300" : "text-zinc-500"}`}>{fmt(r.produced_qty)}</td>
              <td className="px-3 py-3 text-right font-mono text-amber-300 tabular-nums font-bold">{fmt(r.balance_qty)}</td>
              <td className="px-3 py-3 font-mono text-zinc-400">{r.target_date || "—"}</td>
              <td className="px-3 py-3"><span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${risk.cls}`}>{risk.label}</span></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function ProductTable({ rows, totals, loading }) {
  return (
    <table className="w-full text-sm">
      <thead>
        {rows.length > 0 && (
          <tr data-testid="pwo-rollup" className="border-b-2 border-amber-400/50 bg-amber-500/5 text-[11px] font-mono">
            <th className="text-left px-3 py-2.5" colSpan={3}>
              <div className="flex flex-col leading-tight">
                <span className="text-[9px] uppercase tracking-widest text-zinc-500">Sum · {rows.length} product codes</span>
                <span className="font-display text-[13px] text-white">Totals</span>
              </div>
            </th>
            <th className="text-right px-3 py-2.5 text-sky-300 tabular-nums text-base font-bold">{fmt(totals.so_eso)}</th>
            <th className="text-right px-3 py-2.5 text-cyan-300 tabular-nums text-base font-bold">{fmt(totals.production)}</th>
            <th className={`text-right px-3 py-2.5 tabular-nums text-base font-bold ${totals.produced > 0 ? "text-emerald-300" : "text-white"}`}>{fmt(totals.produced)}</th>
            <th className="text-right px-3 py-2.5 text-amber-300 tabular-nums text-base font-bold">{fmt(totals.balance)}</th>
            <th></th>
          </tr>
        )}
        <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
          <th className="text-left px-3 py-3 font-medium">Product Code</th>
          <th className="text-right px-3 py-3 font-medium">WOs</th>
          <th className="text-left px-3 py-3 font-medium">Customers</th>
          <th className="text-right px-3 py-3 font-medium">SO/ESO Qty</th>
          <th className="text-right px-3 py-3 font-medium">Production Qty</th>
          <th className="text-right px-3 py-3 font-medium">Already Produced</th>
          <th className="text-right px-3 py-3 font-medium">Balance Pending</th>
          <th className="text-left px-3 py-3 font-medium">Earliest Date</th>
        </tr>
      </thead>
      <tbody>
        {loading && <tr><td colSpan={8} className="text-center py-16 text-zinc-500">Loading…</td></tr>}
        {!loading && rows.length === 0 && <tr><td colSpan={8} className="text-center py-16 text-zinc-500">No pending Work Orders 🎉</td></tr>}
        {rows.map((r, i) => (
          <tr key={r.product_code} data-testid={`pwo-prod-row-${i}`} className={`border-t border-[var(--border)] ${r.delayed ? "bg-red-500/5" : "hover:bg-[var(--bg-2)]/30"}`}>
            <td className="px-3 py-3 font-mono text-emerald-300">{r.product_code}</td>
            <td className="px-3 py-3 text-right font-mono text-zinc-300">{r.wo_count}</td>
            <td className="px-3 py-3 text-zinc-300 truncate max-w-[280px]" title={r.customers}>{r.customers || "—"}</td>
            <td className="px-3 py-3 text-right font-mono text-sky-300 tabular-nums">{fmt(r.so_eso_qty)}</td>
            <td className="px-3 py-3 text-right font-mono text-cyan-300 tabular-nums">{fmt(r.production_qty)}</td>
            <td className={`px-3 py-3 text-right font-mono tabular-nums ${r.produced_qty > 0 ? "text-emerald-300" : "text-zinc-500"}`}>{fmt(r.produced_qty)}</td>
            <td className="px-3 py-3 text-right font-mono text-amber-300 tabular-nums font-bold">{fmt(r.balance_qty)}</td>
            <td className="px-3 py-3 font-mono text-zinc-400">{r.earliest_date || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Kpi({ label, value, tone = "accent", tid }) {
  const color = { accent: "text-[var(--accent)]", emerald: "text-emerald-400",
    amber: "text-amber-400", blue: "text-blue-400", cyan: "text-cyan-400", red: "text-red-400" }[tone] || "text-white";
  return (
    <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-1)] p-4" data-testid={tid}>
      <div className="overline text-[10px]">{label}</div>
      <div className={`font-display font-black text-2xl mt-1 tabular-nums ${color}`}>{value}</div>
    </div>
  );
}

// PENDING-WO-STATEMENT-01 · active WOs whose pending cannot be measured in Loom metres (excluded from totals).
// NM rows mapped onto the product-wise column slots: WO No | Product Code | Pending Qty (blank) | Reason
export function productCsvRows(productRows, nmRows) {
  return [
    ...productRows,
    {},
    { product_code: "Pending Not Measurable" },
    { product_code: "WO No", wo_count: "Product Code", customers: "Pending Qty", so_eso_qty: "Reason" },
    ...nmRows.map(r => ({ product_code: r.wo_no, wo_count: r.product_code || "", customers: "", so_eso_qty: r.reason })),
  ];
}

function NotMeasurableTable({ rows }) {
  if (!rows.length) return null;
  return (
    <div className="mt-6 border border-amber-500/30 rounded-lg overflow-hidden" data-testid="pwo-not-measurable">
      <div className="px-4 py-2 bg-amber-500/10 text-amber-300 text-sm font-semibold" data-testid="pwo-not-measurable-count">
        Pending Not Measurable · {rows.length} active WO(s) · excluded from totals
      </div>
      <table className="w-full text-sm">
        <thead className="text-zinc-400 text-xs uppercase">
          <tr><th className="text-left px-3 py-2">WO No</th><th className="text-left px-3 py-2">SO/ESO No</th>
            <th className="text-left px-3 py-2">Product Code</th><th className="text-right px-3 py-2">Authorised</th>
            <th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Reason</th></tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="border-t border-zinc-800" data-testid={`pwo-nm-row-${r.wo_no}`}>
              <td className="px-3 py-2 font-mono">{r.wo_no}</td>
              <td className="px-3 py-2">{r.source_order_no || "—"}</td>
              <td className="px-3 py-2">{r.product_code || "—"}</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">{fmt(r.production_qty)}</td>
              <td className="px-3 py-2 text-amber-300">{r.status}</td>
              <td className="px-3 py-2 text-zinc-400">{r.reason}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
